import { createContext, useContext, useState, PropsWithChildren } from "react";
import {
  getPromotionValue,
  hasTradeInPromoCode,
} from "./utils/getPromotionValue";
import { FeatureFlags, Message } from "@/types/types";
import {
  Device,
  TradeInApiError,
} from "./components/TradeInModal/DeviceSearch/types";
import {
  EnquiryResponse,
  GradingAnswer,
} from "./components/TradeInModal/Grading/types";
import {
  AvailablePaymentOption,
  QuotesResponse,
} from "./components/TradeInModal/PaymentOptions/PaymentOptions";
import { TRADE_IN_STEPS } from "./components/TradeInModal/TradeInModal";

type Props = {
  value: {
    messages: Message[];
    micrositeUrl: string;
    tradeInBaseUrl: string;
    basketApiBaseUrl: string;
    productId: string;
    selectedDevice?: Device;
    enquiryOptions?: EnquiryResponse;
    gradingAnswers?: GradingAnswer[];
    quoteDetail?: QuotesResponse;
    paymentOptions?: AvailablePaymentOption[];
    existingQuotes?: QuotesResponse[];
    micrositeOnClick: () => void;
    errorMessage?: TradeInApiError | null;
    getQuotesFromBasket?: boolean;
    flags: FeatureFlags;
  };
};

type ContextProviderProps = {
  hasPromoCode: boolean;
  promotionValue: string;
  micrositeUrl: string;
  tradeInBaseUrl: string;
  basketApiBaseUrl: string;
  micrositeOnClick: () => void;
  onApiError: (error: TradeInApiError) => void;
  errorMessage: TradeInApiError | null;
  setErrorMessage: (errorMessage: TradeInApiError | null) => void;
  selectedDevice: Device | null;
  setSelectedDevice: (device: Device | null) => void;
  productId: string;
  deviceIdentifier: string;
  currentScreen: number;
  enquiryOptions: EnquiryResponse | null;
  gradingAnswers: GradingAnswer[];
  quoteDetail: QuotesResponse | null;
  paymentOptions: AvailablePaymentOption[];
  existingQuotes: QuotesResponse[];
  setDeviceIdentifier: (deviceIdentifier: string) => void;
  setCurrentScreen: (screen: number) => void;
  setEnquiryOptions: (enquiryResponse: EnquiryResponse | null) => void;
  setGradingAnswers: (gradingAnswers: GradingAnswer[]) => void;
  setQuoteDetail: (quotesResponse: QuotesResponse | null) => void;
  setExistingQuotes: (quotesResponses: QuotesResponse[]) => void;
  setPaymentOptions: (paymentOptions: AvailablePaymentOption[]) => void;
  shouldCloseModal: boolean;
  setCloseModalFlag: (shouldClose: boolean) => void;
  resetContextData: () => void;
  getQuotesFromBasket: boolean;
  flags: FeatureFlags;
};
export const TradeInContext = createContext<ContextProviderProps | null>(null);

export const TradeInContextProvider = ({
  children,
  value,
}: PropsWithChildren<Props>) => {
  const {
    messages,
    micrositeUrl,
    tradeInBaseUrl,
    basketApiBaseUrl,
    micrositeOnClick,
    productId,
    getQuotesFromBasket,
    flags,
  } = value;
  const [errorMessage, setErrorMessage] = useState<TradeInApiError | null>(
    (value?.errorMessage && { ...value.errorMessage }) || null,
  );
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(
    (value?.selectedDevice && { ...value.selectedDevice }) || null,
  );
  const [currentScreen, setCurrentScreen] = useState(
    TRADE_IN_STEPS.FIND_DEVICE,
  );
  const [enquiryOptions, setEnquiryOptions] = useState<EnquiryResponse | null>(
    value?.enquiryOptions || null,
  );
  const [gradingAnswers, setGradingAnswers] = useState<GradingAnswer[]>(
    value?.gradingAnswers || [],
  );
  const [quoteDetail, setQuoteDetail] = useState<QuotesResponse | null>(
    value?.quoteDetail || null,
  );
  const [existingQuotes, setExistingQuotes] = useState<QuotesResponse[]>(
    value.existingQuotes || [],
  );
  const [paymentOptions, setPaymentOptions] = useState<
    AvailablePaymentOption[]
  >(value?.paymentOptions || []);
  const [shouldCloseModal, setShouldCloseModal] = useState<boolean>(false);

  const [deviceIdentifier, setDeviceIdentifier] = useState<string>("");

  const hasPromoCode = hasTradeInPromoCode(messages);
  const promotionValue = getPromotionValue(messages);

  const onApiError = (error: TradeInApiError) => {
    setErrorMessage(error);
    setCurrentScreen(TRADE_IN_STEPS.UNAVAILABLE);
  };

  const resetContextData = () => {
    setErrorMessage(null);
    setSelectedDevice(null);
    setCurrentScreen(0);
    setEnquiryOptions(null);
    setQuoteDetail(null);
    setGradingAnswers([]);
    setExistingQuotes([]);
    setPaymentOptions([]);
    setShouldCloseModal(false);
  };

  return (
    <TradeInContext.Provider
      value={{
        hasPromoCode,
        promotionValue,
        micrositeUrl,
        tradeInBaseUrl,
        basketApiBaseUrl,
        productId,
        micrositeOnClick,
        currentScreen,
        setCurrentScreen,
        selectedDevice,
        setSelectedDevice,
        enquiryOptions,
        setEnquiryOptions,
        gradingAnswers,
        setGradingAnswers,
        quoteDetail,
        setQuoteDetail,
        deviceIdentifier,
        setDeviceIdentifier,
        paymentOptions,
        setPaymentOptions,
        existingQuotes,
        setExistingQuotes,
        onApiError,
        errorMessage,
        setErrorMessage,
        shouldCloseModal,
        setCloseModalFlag: setShouldCloseModal,
        resetContextData,
        getQuotesFromBasket: getQuotesFromBasket === true,
        flags,
      }}
    >
      {children}
    </TradeInContext.Provider>
  );
};

export const useTradeInContext = () => {
  const context = useContext(TradeInContext);

  if (!context) {
    throw new Error(
      "useTradeInContext must be used within a TradeInContextProvider",
    );
  }

  return context;
};
